import clsx from 'clsx';
import type { DagNotitie } from '../../stores/controle-store';

interface BankImportPreviewProps {
  iban: string;
  creditsByDay: Map<string, number>;
  filteredTotal: number;
  filteredCount: number;
  dagNotities: Map<string, DagNotitie>;
  isSaving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);
}

function formatDayLabel(datum: string): string {
  const d = new Date(datum + 'T12:00:00');
  const day = d.getDate();
  const month = d.getMonth() + 1;
  const weekday = d.toLocaleDateString('nl-NL', { weekday: 'short' });
  return `${weekday} ${day}-${month}`;
}

export function BankImportPreview({ iban, creditsByDay, filteredTotal, filteredCount, dagNotities, isSaving, onConfirm, onCancel }: BankImportPreviewProps) {
  const sorted = Array.from(creditsByDay.entries()).sort(([a], [b]) => a.localeCompare(b));
  const total = sorted.reduce((sum, [, amount]) => sum + amount, 0);
  const overwriteCount = sorted.filter(([date]) => dagNotities.get(date)?.bank_bedrag != null).length;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onCancel}>
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-3">
          <h3 className="text-lg font-heading font-semibold text-main">Bankafschrift importeren</h3>
          <p className="text-sm text-muted mt-1">
            IBAN: <span className="font-mono">{iban}</span>
          </p>
        </div>

        {/* Summary */}
        <div className="px-5 pb-3">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-muted">
              <strong className="text-main">{sorted.length}</strong> dag{sorted.length !== 1 ? 'en' : ''} met inkomende betalingen
            </span>
            <span className="text-muted">
              Totaal: <strong className="text-main">{formatCurrency(total)}</strong>
            </span>
          </div>
          {filteredCount > 0 && (
            <p className="text-xs text-gray-500 mt-2">
              {filteredCount} transactie{filteredCount !== 1 ? 's' : ''} gefilterd ({formatCurrency(filteredTotal)}) — Mollie/MyPos uitbetalingen en contante stortingen.
            </p>
          )}
          {overwriteCount > 0 && (
            <p className="text-xs text-amber-700 mt-1">
              {overwriteCount} dag{overwriteCount !== 1 ? 'en' : ''} {overwriteCount !== 1 ? 'hebben' : 'heeft'} al een bankbedrag — {overwriteCount !== 1 ? 'worden' : 'wordt'} overschreven.
            </p>
          )}
        </div>

        {/* Table */}
        <div className="px-5 overflow-y-auto flex-1 min-h-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="py-1.5 text-left text-xs font-medium text-muted uppercase">Datum</th>
                <th className="py-1.5 text-right text-xs font-medium text-muted uppercase">Inkomend</th>
                <th className="py-1.5 text-right text-xs font-medium text-muted uppercase w-20">Huidig</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(([date, amount]) => {
                const existing = dagNotities.get(date)?.bank_bedrag;
                return (
                  <tr key={date} className="border-b border-gray-50">
                    <td className="py-1.5 font-mono text-xs text-muted">{formatDayLabel(date)}</td>
                    <td className="py-1.5 text-right tabular-nums font-medium">{formatCurrency(amount)}</td>
                    <td className={clsx('py-1.5 text-right tabular-nums text-xs', existing != null ? 'text-amber-600' : 'text-gray-300')}>
                      {existing != null ? formatCurrency(existing) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-300">
                <td className="py-2 text-xs font-medium text-muted uppercase">Totaal</td>
                <td className="py-2 text-right tabular-nums font-semibold">{formatCurrency(total)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Actions */}
        <div className="px-5 py-4 flex items-center justify-end gap-2 border-t border-gray-100">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100"
          >
            Annuleren
          </button>
          <button
            onClick={onConfirm}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {isSaving ? 'Importeren...' : `Importeren (${sorted.length} dagen)`}
          </button>
        </div>
      </div>
    </div>
  );
}
